import type { Event, EventHint } from '@sentry/angular';

import { whenNoRequestsInFlight } from './requests-in-flight';

// A code file (Angular route chunk or Ionic component chunk) failed to load, usually because a deploy
// replaced it while the tab was open. Safari: "Importing a module script failed"; Chrome: "Failed to
// fetch dynamically imported module"; Firefox: "error loading dynamically imported module". Ionic's
// loader logs the import error itself and then throws "Constructor for "ion-modal#ios" was not found".
const CHUNK_LOAD_ERROR = /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Constructor for "[\w-]+#[\w-]*" was not found/i;
const MAX_DEPTH = 3;

export const CHUNK_RELOAD_STORAGE_KEY = 'tidy_chunk_load_reload_at';
export const CHUNK_RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

export interface ChunkLoadRecoveryDeps {
  storage: () => Pick<Storage, 'getItem' | 'setItem'>;
  reload: () => void;
  /** Ionic's loader fails the moment a button shows its spinner, while the button's save is still in flight. */
  whenIdle: (callback: () => void) => void;
  now: () => number;
  isOnline: () => boolean;
}

export function isChunkLoadError(error: unknown, depth = 0): boolean {
  if (typeof error === 'string') {
    return CHUNK_LOAD_ERROR.test(error);
  }
  if (!error || typeof error !== 'object' || depth > MAX_DEPTH) {
    return false;
  }
  const { message, rejection, reason, cause } = error as Record<string, unknown>;
  // Zone.js wraps an unhandled promise rejection and keeps the original in `rejection`.
  return [message, rejection, reason, cause].some(inner => isChunkLoadError(inner, depth + 1));
}

export type ChunkLoadRecovery = ReturnType<typeof createChunkLoadRecovery>;

export function createChunkLoadRecovery(deps: ChunkLoadRecoveryDeps) {
  let reloadPending = false;
  return {
    /**
     * Reloads the page for a chunk-load error, at most once per cooldown per tab, once no API request is in flight
     * (so a sign-up that already created the account lands on its next page, signed in). True when the error is
     * handled by the reload.
     */
    recover(error: unknown): boolean {
      if (!isChunkLoadError(error)) {
        return false;
      }
      if (reloadPending) {
        return true;
      }
      // Offline, a reload only swaps the stuck page for the browser's offline page.
      if (!deps.isOnline()) {
        return false;
      }
      try {
        const storage = deps.storage();
        const lastReloadAt = Number(storage.getItem(CHUNK_RELOAD_STORAGE_KEY));
        if (lastReloadAt && deps.now() - lastReloadAt < CHUNK_RELOAD_COOLDOWN_MS) {
          return false;
        }
        storage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(deps.now()));
      } catch {
        // Without storage the guard can't stop a reload loop, so don't reload at all.
        return false;
      }
      reloadPending = true;
      deps.whenIdle(deps.reload);
      return true;
    },
  };
}

/**
 * For Sentry's beforeSend, which can see a chunk-load error before our window listeners do: true when the page
 * reloads for the event, so it isn't sent. Only unhandled errors count; an error the app caught and reported
 * itself (such as a failed S3 upload) must not reload the page under the customer's feet.
 */
export function reloadsForSentryEvent(event: Event, hint: EventHint | undefined, recovery: ChunkLoadRecovery = chunkLoadRecovery): boolean {
  const unhandled = event.exception?.values?.some(value => value.mechanism?.handled === false) ?? false;
  return unhandled && recovery.recover(hint?.originalException);
}

interface ZoneWithSymbols {
  __symbol__(name: string): string;
}

/**
 * Ionic's component loader runs outside Angular, so its "Constructor for ... was not found" rejection is a Zone.js
 * promise in the root zone: it never reaches the ErrorHandler, and Zone's forwarder to the window's
 * `unhandledrejection` listeners throws (it builds the event with `promise: undefined`) and swallows the error.
 * So we hook Zone's own unhandled-rejection handler, and keep calling the one it had.
 */
export function listenForZoneUnhandledRejections(zone: ZoneWithSymbols | undefined, recovery: ChunkLoadRecovery = chunkLoadRecovery): void {
  if (!zone?.__symbol__) {
    return;
  }
  const handlerKey = zone.__symbol__('unhandledPromiseRejectionHandler');
  const zoneHandlers = zone as unknown as Record<string, ((this: unknown, error: unknown) => void) | undefined>;
  const forward = zoneHandlers[handlerKey];
  zoneHandlers[handlerKey] = function (this: unknown, error: unknown) {
    recovery.recover(error);
    forward?.call(this, error);
  };
}

/* eslint-disable no-restricted-globals -- also runs before Angular bootstrap, no Platform service available */
export const chunkLoadRecovery = createChunkLoadRecovery({
  storage: () => window.sessionStorage,
  reload: () => window.location.reload(),
  whenIdle: callback => whenNoRequestsInFlight(callback),
  now: () => Date.now(),
  isOnline: () => window.navigator.onLine !== false,
});
/* eslint-enable no-restricted-globals */
